import type { RelationNode, RelationsBlock } from '../../../query-dsl';
import type { ColumnRelation, DataTableColumnDef } from '../types';

/**
 * Builds the Aurora Query DSL `relations` node from columns that declare a
 * `relation` — the DSL successor of the Sequelize `include` tree. The node
 * shapes which children get loaded WITHOUT filtering the parent rows
 * (filtering parents by a relation column is the nested relation object in
 * `where` — see `nestWhereCondition`).
 *
 * Two columns referencing the same association are collapsed into a single
 * key. When their nested chains differ, the chains are merged: e.g.
 *
 *   col A → { association: 'account', include: [{ association: 'user' }] }
 *   col B → { association: 'account', include: [{ association: 'tenant' }] }
 *
 *   getRelationsNode([A, B])
 *   // → { account: { relations: { user: true, tenant: true } } }
 *
 * Source columns are never mutated; the returned node is a fresh object
 * structure safe for the consumer to extend. The function reads the explicit
 * `relation` field — it does NOT scan column ids — so the dependency on a
 * relation is visible at the column definition site.
 *
 * Returns `undefined` when no column declares a relation, so consumers pass
 * the result straight through (no length/emptiness checks):
 *
 * @example
 * // resolver / component wiring:
 * const relations = getRelationsNode(getEntityStandaloneColumns());
 * queryPaginate({ query: { offset: 0, limit: DEFAULT_PAGE_SIZE, order, relations } });
 */
export function getRelationsNode<T>(
  columns: DataTableColumnDef<T>[],
): RelationsBlock | undefined {
  let node: RelationsBlock | undefined;
  for (const col of columns) {
    if (!col.relation) continue;
    node ??= {};
    mergeInto(node, col.relation);
  }
  return node;
}

function mergeInto(target: RelationsBlock, rel: ColumnRelation): void {
  const existing = target[rel.association];

  if (!rel.include || rel.include.length === 0) {
    // Leaf declaration: `true` unless a deeper chain already created a node.
    if (existing === undefined) {
      target[rel.association] = true;
    }
    return;
  }

  // Chain declaration: ensure a node object with a nested `relations` block,
  // upgrading a plain `true` set by a shallower column.
  let relationNode: RelationNode;
  if (existing === undefined || existing === true) {
    relationNode = {};
    target[rel.association] = relationNode;
  } else {
    relationNode = existing;
  }
  relationNode.relations ??= {};
  for (const child of rel.include) {
    mergeInto(relationNode.relations, child);
  }
}
